import clsx from 'clsx';
import { useFormik } from 'formik';
import React, { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as Yup from 'yup';

import { SpinnerContainer } from '@/components/Containers/SpinnerContainer';
import { Button, Spinner } from '@/components/Elements';
import { DisclaimerPhoneValidationV2 } from '@/components/Elements/DisclaimerPhoneValidation/DisclaimerPhoneValidationV2';
import { iSetupData } from '@/components/PublicPages/SetupPublicPage';
import { DEFAULT_THEME } from '@/config';
import { getFormName, renderField } from '@/utils/forms';
import { getMDirectorSupportPhone, getMDirectorSupportPhoneRaw, themes } from '@/utils/helpers';

const FormSetupWrapper = styled.div`
  .form--setup button[type='submit']:focus {
    outline: 2px solid var(--md-mediumGray-color);
  }
`;

const SetupErrorMessage = styled.div`
  .setup--error__message {
    border-left: 10px solid red;
    background-color: #ff7a7a;
    color: #fff;
    padding: 20px;
  }
`;

const SetupInfoMessage = styled.div`
  .setup--info__message {
    padding: 20px;
    background-color: #f6f6f4;
    margin-bottom: 30px;
    font-size: 16px;
  }

  .setup__info__message a {
    color: var(--md-turquoise-color);
  }
`;

export type FormSetupConfirmProps = {
  className?: string;
  initialValues: iSetupData;
  onSubmit: (values: iSetupData) => void;
  onResendToken: () => void;
  isSubmitting: boolean;
  onGoBack: () => void;
  userBlocked: boolean;
  systemError: any;
  actionForm: string;
  systemConfirmSuccess?: boolean;
  tokenResent?: boolean;
  tokenResentError?: boolean;
  tokenLoading?: boolean;
};

export const FormSetupConfirm = ({
  className = '',
  initialValues,
  // onResendToken,
  onSubmit,
  isSubmitting = false,
  onGoBack,
  userBlocked,
  systemError,
  actionForm,
  systemConfirmSuccess = false,
  tokenResent = false,
  tokenResentError = false,
  tokenLoading = false,
}: FormSetupConfirmProps) => {
  const { t } = useTranslation();
  const { phone } = initialValues;
  const formRef: React.RefObject<HTMLFormElement> = createRef();
  const [showGoBack, setShowGoBack] = useState(false);
  const timeInMS = 300000;

  useEffect(() => {
    if (systemConfirmSuccess) {
      formRef.current?.submit();
    }
    setTimeout(() => {
      !showGoBack && setShowGoBack(true);
    }, timeInMS);
  }, [formRef, systemConfirmSuccess, showGoBack]);

  const onFormSubmit = (values: iSetupData) => {
    onSubmit(values);
  };

  const defaultValidationSchema = {
    token: Yup.string().required(t('Forms.required')),
  };

  const onDefaultGoBack = () => {
    onGoBack && onGoBack();
  };

  const getTokenErrors = () => {
    if (tokenResentError) {
      return (
        <div className="token__resend__notification--error text-md-red">
          <p>
            {`${t('Forms.SetupConfirm.tokenResentError')} ${t(
              'Forms.SetupConfirm.tokenResentError2'
            )}`}
          </p>
        </div>
      );
    }
  };

  const getTokenSuccess = () => {
    if (tokenResent) {
      return (
        <div className="token__resend__notification--success text-md-olive">
          <p>{t('Forms.SetupConfirm.tokenResentSuccess')}</p>
        </div>
      );
    }
  };

  const getRetry = () => {
    return (
      <div className="flex flex-row-reverse gap-2.5 items-center">
        <div className="flex gap-2.5">
          {tokenLoading && <Spinner size="sm" className="token__resend " />}
          <Button
            onClick={onDefaultGoBack}
            variant="raw"
            size="free"
            className="text-md-links no-underline hover:underline hover:text-md-links"
            title={t('Forms.TokenCheck.retry')}
          >
            {t('Forms.SetupConfirm.goBack')}
          </Button>
        </div>
      </div>
    );
  };

  const getSystemError = () => {
    if (Object.keys(systemError).length) {
      const { detail } = systemError as any;
      const message = t(`Forms.SetupConfirm.Errors.${detail}`);

      return (
        <SetupErrorMessage>
          <div className="setup--error__message">
            <p className="text-sm">{message}</p>
          </div>
        </SetupErrorMessage>
      );
    }
  };

  const SetupConfirmForm = ({ formik }: any) => {
    return (
      <FormSetupWrapper>
        <form
          method="post"
          ref={formRef}
          action={actionForm}
          onSubmit={formik.handleSubmit}
          className={clsx('form--setup', className)}
          id={getFormName(formik)}
        >
          <div className="mb-3 flex flex-wrap md:flex-nowrap justify-between gap-3">
            {renderField(formik, 'token', {
              customClasses: 'w-full md:w-1/2',
              required: true,
              autoFocus: true,
            })}
          </div>

          {renderField(formik, 'name', { type: 'hidden' })}
          {renderField(formik, 'lastname', { type: 'hidden' })}
          {renderField(formik, 'companyName', { type: 'hidden' })}
          {renderField(formik, 'mobilePrefix', { type: 'hidden' })}
          {renderField(formik, 'phone', { type: 'hidden' })}

          {systemError && getSystemError()}

          <div className="mt-10 flex justify-center lg:justify-between flex-wrap items-center">
            <div className="flex items-center mb-10 lg:mb-0 flex-wrap justify-center lg:justify-start w-full lg:w-auto">
              <Button
                variant="primary"
                type="submit"
                size="free"
                className="py-5 lg:py-3 w-full lg:w-[140px] focus:outline-black outline-2 outline-md-gray"
                isLoading={isSubmitting}
                loadingText={t('common.sending')}
              >
                {t('common.send')}
              </Button>
            </div>
            {showGoBack && getRetry()}
          </div>
          <div className="token__resend__notifications text-right ml-auto max-w-md">
            {getTokenSuccess()}
            {getTokenErrors()}
          </div>
        </form>
      </FormSetupWrapper>
    );
  };

  const MainForm = (props: any) => {
    const { className } = props;
    const formikData = {
      initialValues: { ...initialValues },
      validationSchema: Yup.object(defaultValidationSchema),
      onSubmit: onFormSubmit,
    };

    return (
      <div className={className}>
        <SetupConfirmForm formik={useFormik(formikData)} />
        <DisclaimerPhoneValidationV2 />
      </div>
    );
  };

  const UserBlockedMessage = () => {
    return (
      <SetupInfoMessage>
        <div className="setup--info__message">
          <p>{t('Forms.SetupConfirm.userBlocked')}</p>
          <p>{t('Forms.SetupConfirm.userBlocked2')}</p>
          <p className="mt-28">
            {t('Forms.help')}
            <a
              title={t('PublicFooter.phoneUs')}
              href={`tel:${getMDirectorSupportPhoneRaw()}`}
              className="ml-2 text-md-links no-underline hover:underline hover:text-md-links"
            >
              {getMDirectorSupportPhone()}
            </a>
          </p>
          <a
            rel="noreferrer"
            href="mailto:soporte@mdirector.com"
            className="text-md-links no-underline hover:underline hover:text-md-links"
            title={t('Forms.emailUsTitle')}
            target="_blank"
          >
            {t('Forms.emailUs')}
          </a>
        </div>
      </SetupInfoMessage>
    );
  };

  if (userBlocked) {
    return (
      <div className="max-w-7xl mt-16 m-auto px-4 lg:px-0">
        <div className="max-w-3xl m-auto">
          <UserBlockedMessage />
        </div>
      </div>
    );
  }

  const getSystemConfirmSuccess = () => {
    if (systemConfirmSuccess) {
      return (
        <div>
          <SpinnerContainer className="!min-h-[300px]" />
        </div>
      );
    }
  };

  return (
    <div className="max-w-7xl mt-12 m-auto px-4 lg:px-0">
      <div className="max-w-3xl m-auto text-md-blue">
        <img
          alt="MDirector"
          className="mx-auto"
          src={themes[DEFAULT_THEME].assetsURL + 'globals/src/images/logos/logo_signup_small.png'}
        />
        {getSystemConfirmSuccess()}
        <div className={clsx({ hidden: systemConfirmSuccess })}>
          <h3 className="md__payments__form--h3">{t('Forms.SetupConfirm.description')}</h3>
          <p>
            {t('Forms.SetupConfirm.step1')} {phone}
          </p>
          <p>{t('Forms.SetupConfirm.step2')}</p>
          <MainForm className="mt-8" />
        </div>
      </div>
    </div>
  );
};
